/**
 * VdiStepperFlow — uses the shared CommonStepper from elements.
 * Data is adapted into BaseStepperStep[] with VDI-specific detailsContent rows.
 */
import React from 'react';
import { MdCheckBox, MdHub, MdAssuredWorkload, MdOutlineAddTask } from 'react-icons/md';
import { GrServerCluster } from 'react-icons/gr';
import { RiUserReceived2Line } from 'react-icons/ri';
import {
  FiActivity,
  FiAlertCircle,
  FiInfo,
  FiClock,
  FiServer,
  FiMessageSquare,
} from 'react-icons/fi';
import { CommonStepper } from '@/components/elements/Stepper';
import type { BaseStepperStep, StepStatus } from '@/components/elements/Stepper';
import {
  SpinnerBlock,
  NoDataPlate,
} from '@/components/organism/TableExpand/primitives';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { StatCell } from '@/components/organism/Cards/StatCell';
import PillCell from '@/components/organism/StatusPills/PillCell';
import type { IVDITransactionProps } from '@/utils/types/vdiTransaction';
import { getDateTime } from '@/utils/dateTime';
import { formatDuration, calcDuration as durationBetweenIso } from '@/utils/duration';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { Text } from '@/components/atoms/AppText';
import {
  APP_ACCENT,
  APP_BORDER,
  APP_SEMANTIC,
} from '@/constant/theme/colors.constant';
import { useTheme } from '@/context/theme.context';
import {
  getStatusOfEachStep,
  getStepperSteps,
  getProviderConfigBasedOnPartnerId,
  getLabelBasedOnProviderConfiguration,
} from '@/pages/VdiTransaction/constant';
import { VDI_TYPES } from '@/constant/gatewayTransaction/gatewayTransaction.constant';
import { EAuthTypeMapper } from '@/constant/provider/provider';
import { VDI_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/vdiTransaction.testIds';
import { tid } from '@/utils/testId';

// ── colour / status helpers ───────────────────────────────────────────────────

/**
 * Derive StepStatus directly from the boolean flags returned by getStatusOfEachStep.
 * Prefer this over resolving CSS class-strings — the flags are the canonical source of truth.
 */
const toStepStatus = (
  success: boolean,
  active: boolean,
  prevSuccess: boolean,
): StepStatus => {
  if (success) return 'done';
  if (active) return 'active';
  if (prevSuccess) return 'failed'; // previous step ok but this step failed
  return 'not-reached';
};

/** Resolve a raw AUTH_TYPE string to its display label via EAuthTypeMapper. Falls back to raw value. */
const mapAuthType = (raw: string): string => {
  if (!raw) return raw;
  const mapped = (EAuthTypeMapper as Record<string, string>)[raw.toUpperCase()];
  return mapped ?? raw;
};

/** Four verb forms of a step name, shown as the subtitle badge based on status. */
interface StepLabels {
  /** Shown when status is 'not-reached' — base verb, e.g. "Accept" */
  pending: string;
  /** Shown when status is 'active' — progressive, e.g. "Accepting" */
  active: string;
  /** Shown when status is 'done' — past form, e.g. "Accepted" */
  done: string;
  /** Shown when status is 'failed' — failure form, e.g. "Acceptance Failed" */
  failed: string;
}

/** Per-state tooltip hints for a step. */
interface StepTooltips {
  pending: string;
  active: string;
  done: string;
  failed: string;
}

/** Pick the correct verb form based on step status. */
const resolveStepLabel = (l: StepLabels, status: StepStatus): string => {
  if (status === 'done') return l.done;
  if (status === 'failed') return l.failed;
  if (status === 'active') return l.active;
  return l.pending;
};

/** Build a contextual tooltip from the step tooltips + current status. */
const resolveTooltip = (t: StepTooltips, status: StepStatus): string =>
  status === 'not-reached' ? t.pending : t[status];

interface StepVM {
  // ── Static definition ──
  stepKey: string;
  labels: StepLabels;
  tooltips: StepTooltips;
  badge?: string;
  icon: React.ReactNode;
  // ── Runtime data ──
  status: StepStatus;
  rawDate?: string | null;
  hoverContent: string;
  description: string;
  serverLabel?: string;
  excluded?: boolean;
}

/** Returns true when a step has a real timestamp that can anchor a duration. */
const hasTimestamp = (s: StepVM): boolean =>
  (s.status === 'done' || s.status === 'failed') && !!s.rawDate;

// ── VDI detail panel rows (injected as detailsContent into CommonStepper) ─────
const VdiStepDetailRows = ({ step }: { step: StepVM }) => {
  const state = step.status;

  const pillPreset =
    state === 'done' ? 'active' : state === 'failed' ? 'inactive' : undefined;
  const pillConfig =
    state === 'active'
      ? {
        bg: APP_SEMANTIC.info.bg,
        text: APP_SEMANTIC.info.text,
        dot: APP_ACCENT.indigo.bg,
        pulse: true,
      }
      : state === 'not-reached'
        ? {
          bg: APP_SEMANTIC.neutral.bg,
          text: APP_SEMANTIC.neutral.text,
          dot: APP_ACCENT.gray.bg,
        }
        : undefined;
  const statusLabel =
    state === 'done'
      ? 'Completed'
      : state === 'active'
        ? 'In Progress'
        : state === 'failed'
          ? 'Failed'
          : 'Pending';
  const server = step.serverLabel ||
    (step.description ? step.description.match(/\(([^)]+)\)/)?.[1] : null);

  const extraCols = (server ? 1 : 0) + (step.hoverContent ? 1 : 0);
  const cols = 2 + extraCols;
  const colClass =
    ({ 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' } as Record<number, string>)[cols] ??
    'grid-cols-2 sm:grid-cols-3';

  return (
    <div
      className={`grid ${colClass} divide-y sm:divide-y-0 sm:divide-x ${APP_BORDER.divideDefault}`}
    >
      <StatCell
        label={ORGANISM_PROPS.VDI_STEPPER.LABEL_STATUS}
        labelIcon={<FiActivity className="w-3 h-3 shrink-0" />}
        value={
          <PillCell
            value={statusLabel}
            preset={pillPreset}
            config={pillConfig}
            dot
          />
        }
      />
      <StatCell
        label={ORGANISM_PROPS.VDI_STEPPER.LABEL_TIMING}
        labelIcon={<FiClock className="w-3 h-3 shrink-0" />}
        value={
          step.rawDate ? getDateTime(step.rawDate) : '—'
        }
        mono
      />
      {server && (
        <StatCell
          label={ORGANISM_PROPS.VDI_STEPPER.LABEL_VIA}
          labelIcon={<FiServer className="w-3 h-3 shrink-0" />}
          value={server}
        />
      )}
      {step.hoverContent && (
        <StatCell
          label={ORGANISM_PROPS.VDI_STEPPER.LABEL_MESSAGE}
          labelIcon={<FiMessageSquare className="w-3 h-3 shrink-0" />}
          value={step.hoverContent}
          mono
        />
      )}
    </div>
  );
};

// ── VdiStepperFlow ────────────────────────────────────────────────────────────
export interface VdiStepperFlowProps {
  data: IVDITransactionProps;
  rowIndex?: number;
  providersListArray: any[];
  loading?: boolean;
  testId?: string;
  hasConfig?: boolean;
}

export const VdiStepperFlow = ({
  data,
  rowIndex,
  providersListArray,
  loading = false,
  testId,
  hasConfig
}: VdiStepperFlowProps) => {
  useTheme();

  // Use configs from the fetched provider directly (avoids partnerId mismatch)
  const providerFromList = providersListArray?.[0];
  const providerConfigurationData = providerFromList?.configs ?? getProviderConfigBasedOnPartnerId(
    data?.partnerId,
    providersListArray,
  );
  const stepperSteps = getStepperSteps(providerConfigurationData);
  const { hasPartnerStep, hasClientStep } = stepperSteps;

  const vdiSettings = providerConfigurationData?.VDI_SETTINGS;
  const labels = vdiSettings
    ? getLabelBasedOnProviderConfiguration(vdiSettings, data?.type)
    : { PARTNER_FLOW_LABEL: '', CLIENT_FLOW_LABEL: '', ACCEPTANCE_STEP_SERVER_LABEL: '', PROCESSING_STEP_SERVER_LABEL: '' };

  // Which VDI_TYPES array does this transaction type belong to?
  // mmsMarkets/mmsProducts → in MMS.VDI_TYPES → partner=MMS, first server=VMS
  // mmsDevices/mmsSales   → in VMS.VDI_TYPES → partner=VMS, first server=MMS
  const typeInMMS = !!(vdiSettings?.MMS?.VDI_TYPES?.includes(data?.type));
  const typeInVMS = !!(vdiSettings?.VMS?.VDI_TYPES?.includes(data?.type));

  const firstServerType = typeInMMS ? 'VMS' : typeInVMS ? 'MMS' : '';
  const partnerServerType = typeInMMS ? 'MMS' : typeInVMS ? 'VMS' : '';
  const firstServer = typeInMMS ? vdiSettings?.VMS : typeInVMS ? vdiSettings?.MMS : null;
  const partnerServer = typeInMMS ? vdiSettings?.MMS : typeInVMS ? vdiSettings?.VMS : null;

  const firstServerAuthType = mapAuthType(firstServer?.AUTH?.AUTH_TYPE ?? '');
  const partnerServerAuthType = mapAuthType(partnerServer?.AUTH?.AUTH_TYPE ?? '');

  const firstBadge = firstServerType && firstServerAuthType ? `${firstServerType} (${firstServerAuthType})` : firstServerType || undefined;
  const partnerBadge = partnerServerType && partnerServerAuthType ? `${partnerServerType} (${partnerServerAuthType})` : partnerServerType || undefined;

  const stepData = getStatusOfEachStep(data, providersListArray);
  const { ACCEPTANCE, GATEWAY, PROCESSED, SYNC_TO_PARTNER, SYNC_TO_CLIENT, ALL_STEPS_STATUS } =
    stepData;
  const {
    acceptanceStepStatus,
    gatewayStepStatus,
    processingStepStatus,
    syncToPartnerStepStatus,
    syncToClientStepStatus,
  } = ALL_STEPS_STATUS;

  const partnerLabel = labels.PARTNER_FLOW_LABEL
    ? mapAuthType(labels.PARTNER_FLOW_LABEL)
    : '';
  const clientLabel = labels.CLIENT_FLOW_LABEL
    ? mapAuthType(labels.CLIENT_FLOW_LABEL)
    : '';

  const S = ORGANISM_PROPS.VDI_STEPPER;

  const rawSteps: StepVM[] = [
    {
      stepKey: 'created',
      labels: { pending: S.STEP_CREATE, active: S.STEP_CREATING, done: S.STEP_CREATED, failed: S.STEP_CREATE_FAILED },
      tooltips: { pending: S.TOOLTIP_CREATE_PENDING, active: S.TOOLTIP_CREATE_ACTIVE, done: S.TOOLTIP_CREATE_DONE, failed: S.TOOLTIP_CREATE_FAILED },
      badge: firstBadge,
      icon: <MdOutlineAddTask />,
      status: data?.transactionDtm ? 'done' : 'not-reached',
      rawDate: data?.transactionDtm ?? null,
      hoverContent: '',
      description: S.STEP_CREATED_SUBTITLE,
    },
    {
      stepKey: 'accepted',
      labels: { pending: S.STEP_ACCEPT, active: S.STEP_ACCEPTING, done: S.STEP_ACCEPTED, failed: S.STEP_ACCEPT_FAILED },
      tooltips: { pending: S.TOOLTIP_ACCEPT_PENDING, active: S.TOOLTIP_ACCEPT_ACTIVE, done: S.TOOLTIP_ACCEPT_DONE, failed: S.TOOLTIP_ACCEPT_FAILED },
      badge: labels.ACCEPTANCE_STEP_SERVER_LABEL || undefined,
      icon: <MdCheckBox />,
      status: toStepStatus(acceptanceStepStatus.success, acceptanceStepStatus.active, true),
      rawDate: data?.createdOn ?? null,
      hoverContent: ACCEPTANCE.hoverContent,
      description: ACCEPTANCE.description,
      serverLabel: labels.ACCEPTANCE_STEP_SERVER_LABEL || undefined,
    },
    {
      stepKey: 'queued',
      labels: { pending: S.STEP_QUEUE, active: S.STEP_QUEUING, done: S.STEP_QUEUED, failed: S.STEP_QUEUE_FAILED },
      tooltips: { pending: S.TOOLTIP_QUEUE_PENDING, active: S.TOOLTIP_QUEUE_ACTIVE, done: S.TOOLTIP_QUEUE_DONE, failed: S.TOOLTIP_QUEUE_FAILED },
      badge: S.STEP_GATEWAY_BADGE,
      icon: <MdHub />,
      status: toStepStatus(gatewayStepStatus.success, gatewayStepStatus.active, acceptanceStepStatus.success),
      rawDate: data?.internalSentOn ?? null,
      hoverContent: GATEWAY.hoverContent,
      description: GATEWAY.description,
    },
    {
      stepKey: 'processed',
      labels: { pending: S.STEP_PROCESS, active: S.STEP_PROCESSING, done: S.STEP_PROCESSED, failed: S.STEP_PROCESS_FAILED },
      tooltips: { pending: S.TOOLTIP_PROCESS_PENDING, active: S.TOOLTIP_PROCESS_ACTIVE, done: S.TOOLTIP_PROCESS_DONE, failed: S.TOOLTIP_PROCESS_FAILED },
      badge: labels.PROCESSING_STEP_SERVER_LABEL || undefined,
      icon: <MdAssuredWorkload />,
      status: toStepStatus(processingStepStatus.success, processingStepStatus.active, gatewayStepStatus.success),
      rawDate: data?.internalProcessedOn ?? null,
      hoverContent: PROCESSED.hoverContent,
      description: PROCESSED.description,
      serverLabel: labels.PROCESSING_STEP_SERVER_LABEL || undefined,
    },
    {
      stepKey: 'partner-sync',
      labels: { pending: S.STEP_SYNC, active: S.STEP_SYNCING, done: S.STEP_SYNCED, failed: S.STEP_SYNC_FAILED },
      tooltips: { pending: S.TOOLTIP_SYNC_PENDING, active: S.TOOLTIP_SYNC_ACTIVE, done: S.TOOLTIP_SYNC_DONE, failed: S.TOOLTIP_SYNC_FAILED },
      badge: partnerBadge,
      icon: <GrServerCluster />,
      status: toStepStatus(syncToPartnerStepStatus.success, syncToPartnerStepStatus.active, processingStepStatus.success),
      rawDate: data?.processedOn ?? null,
      hoverContent: SYNC_TO_PARTNER.hoverContent,
      description: SYNC_TO_PARTNER.description,
      serverLabel: partnerLabel || undefined,
      excluded: !hasPartnerStep,
    },
    {
      stepKey: 'client-sync',
      labels: { pending: S.STEP_REPORT, active: S.STEP_REPORTING, done: S.STEP_REPORTED, failed: S.STEP_REPORT_FAILED },
      tooltips: { pending: S.TOOLTIP_REPORT_PENDING, active: S.TOOLTIP_REPORT_ACTIVE, done: S.TOOLTIP_REPORT_DONE, failed: S.TOOLTIP_REPORT_FAILED },
      badge: clientLabel ? `CLIENTS (${clientLabel})` : undefined,
      icon: <RiUserReceived2Line />,
      status: toStepStatus(
        syncToClientStepStatus.success,
        syncToClientStepStatus.active,
        hasPartnerStep ? syncToPartnerStepStatus.success : processingStepStatus.success,
      ),
      rawDate: data?.sentOn ?? null,
      hoverContent: SYNC_TO_CLIENT.hoverContent,
      description: SYNC_TO_CLIENT.description,
      serverLabel: clientLabel || undefined,
      excluded: !hasClientStep,
    },
  ];

  let visibleSteps = rawSteps.filter((s) => !s.excluded);
  if (VDI_TYPES.findIndex((t: any) => t.id === data?.type) > 4) {
    visibleSteps = visibleSteps.slice(0, 3);
  }

  // In a sequential flow only the FIRST non-done step keeps its status;
  // every subsequent non-done step is forced to 'not-reached'.
  const normalizedSteps: StepVM[] = visibleSteps.map((s, i, arr) => {
    if (s.status === 'done') return s;
    const isFirst = !arr.slice(0, i).some((prev) => prev.status !== 'done');
    return isFirst ? s : { ...s, status: 'not-reached' as StepStatus };
  });

  // Adapt StepVM[] → BaseStepperStep[] with durationToNext
  const steps: BaseStepperStep[] = normalizedSteps.map((s, i) => {
    const next = normalizedSteps[i + 1];
    const rowKey = `table-row-${rowIndex ?? 0}`;
    const stepId = `${s.stepKey}-${rowKey}`;
    const dur = hasTimestamp(s) && next && hasTimestamp(next)
      ? durationBetweenIso(s.rawDate, next.rawDate)
      : undefined;
    return {
      title: s.badge ?? '',
      subtitle: resolveStepLabel(s.labels, s.status),
      date: s.rawDate ?? undefined,
      status: s.status,
      testId: tid(TID.STEPPER_STEP(stepId)),
      durationTestId: i > 0 ? tid(TID.STEPPER_DURATION_TO(stepId)) : undefined,
      labelTestId: tid(TID.STEPPER_LABEL(stepId)),
      dateTestId: tid(TID.STEPPER_TIMESTAMP(stepId)),
      durationValueTestId:
        i > 0 ? tid(TID.STEPPER_DURATION_VALUE(stepId)) : undefined,
      iconElement: s.icon,
      tooltipContent: resolveTooltip(s.tooltips, s.status),
      durationToNext: dur,
      detailsContent: s.status !== 'not-reached'
        ? <VdiStepDetailRows step={s} />
        : undefined,
    };
  });

  const renderStepperContent = () => {
    const isLoaderVisible = hasConfig && loading;
    const isStepperVisible = hasConfig && !loading;
    const isNoDataVisible = !hasConfig
      && !loading;

    if (isLoaderVisible) {
      return (
        <div className="px-4 py-6">
          <SpinnerBlock message={ORGANISM_PROPS.VDI_STEPPER.LOADING_MSG} />
        </div>
      );
    }

    if (isStepperVisible) {
      return <CommonStepper steps={steps} />;
    }

    if (isNoDataVisible) {
      return (
        <div className="px-4 py-6" data-testid={tid(TID.STEPPER_NO_CONFIG(`table-row-${rowIndex ?? 0}`))}>
          <NoDataPlate
            message={ORGANISM_PROPS.VDI_STEPPER.NO_CONFIG_MSG}
            icon={<FiAlertCircle className="h-8 w-8" />}
          />
        </div>
      );
    }
    return null;
  };
  return (
    <SectionCard
      testId={testId}
      title={ORGANISM_PROPS.VDI_STEPPER.SECTION_TITLE}
      icon={<FiActivity className="h-3.5 w-3.5" />}
      compact
      bodyClassName=""
    >
      {/* Flow tags + hint */}
      {(hasPartnerStep || hasClientStep) && (
        <div
          className={`flex items-center gap-2 px-4 py-2 border-b ${APP_BORDER.soft}`}
        >
          {hasPartnerStep && (
            <Text
              as="span"
              size="xs"
              weight="semibold"
              className={`px-2 py-0.5 rounded-full ${APP_SEMANTIC.orange.bg} ${APP_SEMANTIC.orange.text} border ${APP_SEMANTIC.orange.border}`}
            >
              {ORGANISM_PROPS.VDI_STEPPER.PARTNER_FLOW}
            </Text>
          )}
          {hasClientStep && (
            <Text
              as="span"
              size="xs"
              weight="semibold"
              className={`px-2 py-0.5 rounded-full ${APP_SEMANTIC.purple.bg} ${APP_SEMANTIC.purple.text} border ${APP_SEMANTIC.purple.border}`}
            >
              {ORGANISM_PROPS.VDI_STEPPER.CLIENT_FLOW}
            </Text>
          )}
          <Text
            as="span"
            size="xs"
            color="subtle"
            className="ml-auto flex items-center gap-1"
          >
            <FiInfo className="w-3 h-3" />{' '}
            {ORGANISM_PROPS.VDI_STEPPER.CLICK_STEP_HINT}
          </Text>
        </div>
      )}
      {renderStepperContent()}
    </SectionCard>
  );
};
